/**
 * The quote builder — `design/mockups/17-quote-v2/quote-main.png`.
 *
 * One page, three layouts (rule 3 of the plan): a 340px rail beside the work
 * on a desktop, a sticky footer instead of the rail on an iPad, and a single
 * column with a bottom bar on a phone. The rows are the same three components
 * in all three.
 *
 * The state here is deliberately thin. There is exactly one piece of server
 * state — the `QuoteTree` — and every mutation replaces it wholesale with what
 * the server sent back. Nothing is patched locally and nothing is
 * recalculated: rule 3 again, from the client's end. What that costs is a
 * round trip per committed edit. What it buys is that this file has no
 * arithmetic in it at all, so it cannot disagree with the proposal, the
 * invoice or the approved total.
 */
import { Fragment, useState, type ReactNode } from 'react'
import type { MeResponse, QuoteVisitView } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { Button, EmptyState } from '../components/primitives.js'
import { Toast } from '../components/overlays.js'
import { IconPlus } from '../frame/icons.js'
import { LineRow } from '../builder/rows/line-row.js'
import { ServiceRow } from '../builder/rows/service-row.js'
import { VisitCard, VisitConnector, followsAbove } from '../builder/rows/visit-card.js'
import { useQuoteBuilder, type Tab } from '../builder/adapters/quote.js'
import { PackageGroup } from '../quote/package-group.js'
import { PlansGrid } from '../quote/plans-grid.js'
import { BeforeYouSendCard, MarginCard, QuoteTotalCard, TermsCard } from '../quote/quote-rail.js'
import { BillingTab } from '../quote/billing/billing-tab.js'
import { BillingToolbar } from '../quote/billing/billing-toolbar.js'
import {
  BillingChecksCard, CashTimelineCard, DefaultsCard, billingBar,
} from '../quote/billing/rail.js'
import { ProposalTab } from '../quote/proposal-tab.js'
import {
  PhoneLineRow, PhoneServiceCard, PhoneServiceHeader, PhoneTabs, PhoneVisitCard, PhoneVisitConnector,
} from '../quote/quote-phone.js'
import { PackageChip, Toolbar } from '../quote/quote-toolbar.js'
import { StickyTotals } from '../quote/sticky-totals.js'
import { QuoteHeader } from '../quote/quote-header.js'
import { QuoteEditorLayer } from '../quote/quote-editor-layer.js'
import { ApprovalRefusedBand, useApprovalRefusal } from '../quote/approval-refused.js'
import { QuoteBand, stagePrimary, useQuoteStage } from '../quote/quote-stage.js'
import { QuoteStagePopups } from '../quote/quote-stage-popups.js'
import { QuoteActivity } from '../quote/quote-activity.js'
import { PROPOSAL_TAB } from '../job-stages/copy.js'

export function QuoteBuilderV2({ me }: { me: MeResponse }) {
  const qb = useQuoteBuilder(me)
  /* What the customer sent is marked handled on any quote, by anyone who can edit quotes. */
  const canHandle = qb.canHandle
  /* The Billing tab's search (step 21 item 5.1), the toolbar's box. */
  const [planSearch, setPlanSearch] = useState('')
  /*
   * An approval its visits refused (`docs/visit-page.md` §26.3, Board 17 G):
   * the band under the header, and Try again sending the same request.
   */
  const refused = useApprovalRefusal({
    quoteId: qb.id,
    status: qb.quote?.status,
    onApproved: qb.load,
    onError: qb.setError,
  })
  /* Folder 52, Board 2: the bar's buttons, the bands and the popups behind them. */
  const stage = useQuoteStage(qb)
  const {
    id, navigate, phone, narrow, quote, error, busy, sentNote, setSentNote, selected,
    setSelected, collapsed, setCollapsed, railOn, setRailOn, setMenu, editing, setEditing, marginScope,
    setMarginScope, summaryOpen, setSummaryOpen, anchor, searchBox, packageBox, addVisitBox, setOpenService,
    proposal, setProposal, proposalError, selection, setSelection, tab, openVisitId,
    openTab, canEdit, mutate, toggle, allCollapsed, collapseAll, goToCheck, blocking,
    hasSubs, railHidden, openVisit, openServiceEditor, openMeasure, openMargin, fixCosts, openLine, openAdd,
    visitMenu, serviceMenu, lineMenu,
  } = qb

  if (error && !quote) {
    return (
      <EmptyState
        title="Not found"
        body={error}
        action={{ label: 'Back to jobs', onClick: () => navigate('/jobs') }}
      />
    )
  }
  if (!quote) return <p className="p-4 text-cell text-text-muted">Loading…</p>

  /*
   * The rail, as a sheet (qb-08 C, qb-09 · 3): the same three cards, so the
   * phone, the iPad and the desktop read one set of components.
   */
  /* The rail's three cards, which the summary sheet lays out by device (qb-08 C, qb-09 · 3). */
  /*
   * The Build tab's card leaves out the Billing tab's quiet rows — "Payments
   * add up to the visits" is that tab's to say — and keeps any that need you.
   */
  const buildChecks = quote.checks.filter((check) => !(check.scope === 'billing' && check.level === 'ok'))
  /* The questions with choices of their own: a visit in one and in no choice is in no price (step 22 item 2.2). */
  const choiceGroups = new Set(quote.chooseOptions.filter((one) => !one.declined).map((one) => one.groupId))
  const summaryCards = quote && {
    total: (
      <QuoteTotalCard
        groupsWithChoices={choiceGroups}
        totals={quote.totals}
        visits={quote.visits}
        onOpenVisit={(visitId) => {
          setSummaryOpen(false)
          setSelected(visitId)
          document.getElementById(`visit-${visitId}`)
            ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
        }}
      />
    ),
    margin: (
      <MarginCard
        margin={quote.totals.margin}
        scope={marginScope}
        onScope={setMarginScope}
        onFixCosts={(lineId) => { setSummaryOpen(false); fixCosts(lineId) }}
        hasSubs={hasSubs}
      />
    ),
    checks: (
      <BeforeYouSendCard
        checks={buildChecks}
        onGo={(check) => { setSummaryOpen(false); goToCheck(check) }}
        /*
         * qb-08 C: the sheet's margin row offers Review › as the rail's does.
         * The sheet closes first — the visit's margin popover would otherwise
         * open underneath it — and the popover hangs off that visit's own
         * chip on the page, scrolled into view, because the Review › it was
         * pressed on is gone with the sheet. On a phone the popover is a
         * sheet of its own, so the anchor only takes focus back.
         */
        onReview={(visitId, at) => {
          setSummaryOpen(false)
          const card = document.getElementById(`visit-${visitId}`)
          card?.scrollIntoView({ block: 'center' })
          openMargin(visitId, card?.querySelector<HTMLElement>('[data-margin-chip]') ?? at)
        }}
      />
    ),
  }

  /* The Billing tab's rail (§8.1): the same three cards in the rail and in the bar's Summary (§8.4). */
  const billingRail = {
    cash: <CashTimelineCard quote={quote} />,
    checks: (
      <BillingChecksCard
        checks={quote.checks}
        onGo={(check) => { setSummaryOpen(false); goToCheck(check) }}
      />
    ),
    defaults: (
      <DefaultsCard
        settings={quote.billingDefaults}
        onOpenSettings={() => { setSummaryOpen(false); navigate('/settings/billing') }}
      />
    ),
  }
  const billing = tab === 'billing'
    ? { ...billingBar(quote), cards: billingRail }
    : undefined

  /*
   * Which visits belong to which package, and which belong to nobody.
   * `packageInstanceId` is the whole rule: a detached group's visits null it
   * out and fall back here, which is exactly what detaching means.
   */
  const byInstance = new Map<string, QuoteVisitView[]>()
  const loose: QuoteVisitView[] = []
  for (const visit of quote.visits) {
    if (!visit.packageInstanceId) { loose.push(visit); continue }
    const list = byInstance.get(visit.packageInstanceId) ?? []
    list.push(visit)
    byInstance.set(visit.packageInstanceId, list)
  }

  /*
   * A visit, wherever it sits. A package's visits are drawn inside its group
   * and the rest are drawn loose, and both have to be the same card: a second
   * rendering of a visit is a second set of bugs in the one thing people look
   * at all day.
   */
  /** The choices a visit is in, by name, in the question's own order (§9.1). */
  const choicesOf = (visitId: string) => quote.chooseOptions
    .filter((option) => !option.declined && option.visitIds.includes(visitId))
    .map((option) => option.name)

  const renderVisit = (visit: QuoteVisitView, chip?: ReactNode) => (
    <div key={visit.id} id={`visit-${visit.id}`}>
      <VisitCard
        visit={visit}
        chip={chip}
        selected={selected === visit.id}
        collapsed={collapsed.has(visit.id)}
        targetMarginPct={quote.totals.targetMarginPct}
        onSelect={() => setSelected(visit.id)}
        onToggle={() => toggle(visit.id)}
        onEditName={openVisit(visit.id, 'name')}
        onEditSchedule={openVisit(visit.id, 'schedule')}
        onEditBilling={openVisit(visit.id, 'billing')}
        onEditNotes={openVisit(visit.id, 'notes')}
        onEditProposal={(at) => {
          anchor.current = at
          setEditing({ kind: 'proposal', visitId: visit.id })
        }}
        onQty={(qty) => void mutate(
          `/visits/${visit.id}`, { method: 'PATCH', body: { qty } })}
        onAddService={(at) => {
          anchor.current = at
          setEditing({ kind: 'addService', visitId: visit.id })
        }}
        onMenu={(at) => setMenu({ ...at, items: visitMenu(visit) })}
        readOnly={!canEdit}
        marginOpen={editing?.kind === 'margin' && editing.visitId === visit.id}
        onMargin={(at) => openMargin(visit.id, at)}
        choices={choicesOf(visit.id)}
        onChoices={openVisit(visit.id, 'choices')}
      >
        {visit.services.map((service) => (
          <ServiceRow
            key={service.id}
            service={service}
            fromPackage={Boolean(visit.packageInstanceId)}
            collapsed={collapsed.has(service.id)}
            onToggle={() => { setOpenService(service.id); toggle(service.id) }}
            onEditName={openServiceEditor(service.id)}
            onEditMeasure={openMeasure(service.id)}
            measureOpen={editing?.kind === 'measure' && editing.serviceId === service.id}
            onAdd={(kind, at) => openAdd(service.id, kind, at)}
            onMenu={(at) => setMenu({ ...at, items: serviceMenu(service.id, at.anchor) })}
            readOnly={!canEdit}
          >
            {service.lines.map((line) => (
              <LineRow
                key={line.id}
                line={line}
                onQty={(qty) => mutate(
                  `/lines/${line.id}`, { method: 'PATCH', body: { qty } })}
                onOpen={openLine(line.id, service.id)}
                onMenu={(at) => setMenu({
                  ...at,
                  items: lineMenu(line.id, line.priceEdited, at.anchor),
                })}
                readOnly={!canEdit}
              />
            ))}
          </ServiceRow>
        ))}
        </VisitCard>
    </div>
  )

  /*
   * The phone's screens (§10.3, qb-09 · 1, qb-10 · 4): the quote; one
   * service's own screen, which is where a visit's Open › goes when the visit
   * holds one service; and, for a visit holding several, the visit's list of
   * them, each opening its own. Which one is in the URL — `?visit=` and
   * `?service=` — so a reload stays put, and both are set or cleared together
   * so a service can never be left open on a visit it is not part of.
   */
  const phoneVisit = phone && openVisitId
    ? quote.visits.find((row) => row.id === openVisitId)
    : undefined
  const phoneService = phoneVisit?.services.find((one) => one.id === qb.params.get('service'))
  const openPhoneScreen = (visitId: string | null, serviceId: string | null) => {
    qb.setParams((was) => {
      const next = new URLSearchParams(was)
      if (visitId) next.set('visit', visitId)
      else next.delete('visit')
      if (serviceId) next.set('service', serviceId)
      else next.delete('service')
      return next
    })
  }

  return (
    /*
     * One page, one scroll (Aaron, step 21 item 6). The builder was the only
     * screen in the product with a pinned header: the title, the tabs and
     * the toolbar sat outside an inner scroll box and everything else slid
     * under them. Now the frame's <main> scrolls the whole of it — header,
     * tabs, toolbar, the choices, the visits and the rail together — and only
     * the app's own top bar stays, as it does everywhere else. `min-h-full`
     * so the iPad's bar still sits on the bottom edge of a short quote.
     */
    <div className="flex min-h-full flex-col">
      {/* The service screen carries its own header in the quote's place (qb-10 · 4). */}
      {!(tab === 'build' && phoneService) && <QuoteHeader qb={qb} stage={stage} />}

      {/* Board 2's grey band on a closed quote, the amber one on a draft while another is out (§6). */}
      {!(tab === 'build' && phoneService) && <QuoteBand quote={quote} actions={stage.actions} phone={phone} />}

      {refused.refusal && (
        <ApprovalRefusedBand
          refusal={refused.refusal}
          busy={busy || refused.busy}
          onRetry={(body) => void refused.approve(body)}
        />
      )}

      {error && (
        <p className="m-0 border-b border-danger-ring bg-danger-bg px-6 py-2 text-body text-danger">
          {error}
        </p>
      )}

      {sentNote && (
        <div className="fixed bottom-4 left-1/2 z-50 w-[min(560px,calc(100vw-32px))] -translate-x-1/2">
          <Toast tone={sentNote.tone} onDismiss={() => setSentNote(null)}>{sentNote.text}</Toast>
        </div>
      )}

      {/*
        * The phone's service screen (qb-10 · 4): its own header, the measure
        * card, the lines and the add links, over a bar that speaks for the
        * visit. It replaces the whole Build tab rather than nesting inside it,
        * because a screen that is a drill-in is a screen, not a panel.
        */}
      {tab === 'build' && phoneVisit && phoneService && (() => {
        const visit = phoneVisit
        const service = phoneService
        /* Up one: to the visit's list when it holds several services, else to the quote. */
        const several = visit.services.length > 1
        return (
          <div className="flex-1 bg-bg pb-[190px]">
            <PhoneServiceHeader
              service={service}
              backLabel={several ? 'Back to the visit' : 'Back to the quote'}
              onBack={() => openPhoneScreen(several ? visit.id : null, null)}
              onEditName={openServiceEditor(service.id)}
              onMenu={canEdit
                ? (at) => setMenu({ ...at, items: serviceMenu(service.id) })
                : undefined}
            />
            <PhoneServiceCard
              service={service}
              canEdit={canEdit}
              open
              screen
              onEditMeasure={openMeasure(service.id)}
              onAdd={(kind, at) => openAdd(service.id, kind, at)}
              margin={{
                pct: service.marginPct,
                targetPct: quote.totals.targetMarginPct,
                open: editing?.kind === 'margin' && editing.visitId === visit.id,
                onOpen: (at) => openMargin(visit.id, at),
              }}
            >
              {service.lines.map((line) => (
                <PhoneLineRow
                  key={line.id}
                  line={line}
                  onQty={(qty) => mutate(
                    `/lines/${line.id}`, { method: 'PATCH', body: { qty } })}
                  onOpen={openLine(line.id, service.id)}
                  targetPct={quote.totals.targetMarginPct}
                  readOnly={!canEdit}
                />
              ))}
            </PhoneServiceCard>
          </div>
        )
      })()}

      {/*
        * The phone's visit screen, for a visit holding no service or several:
        * each service a row that opens its own screen.
        */}
      {tab === 'build' && phoneVisit && !phoneService && (() => {
        const visit = phoneVisit
        return (
          <div className="flex-1 bg-bg px-4 pb-[190px] pt-3">
            <div className="mb-3">
              <button
                type="button"
                onClick={openVisit(visit.id, 'name')}
                className="text-[18px] font-bold text-text"
              >
                {visit.name}
              </button>
              <p className="m-0 mt-0.5 text-[12.5px] text-text-muted">
                {visit.phrase.card} · {visit.billingPlanName ?? 'No billing plan'}
              </p>
            </div>

            <div className="flex flex-col gap-2.5">
              {visit.services.map((service) => (
                <PhoneServiceCard
                  key={service.id}
                  service={service}
                  canEdit={canEdit}
                  open={false}
                  onToggle={() => {
                    setOpenService(service.id)
                    openPhoneScreen(visit.id, service.id)
                  }}
                  onEditMeasure={openMeasure(service.id)}
                  onAdd={(kind, at) => openAdd(service.id, kind, at)}
                />
              ))}
            </div>

            {canEdit && (
              <Button
                variant="primary"
                className="mt-3 h-[50px] w-full rounded-touch text-[15px]"
                onClick={() => void mutate(
                  `/visits/${visit.id}/services`, { method: 'POST', body: {} })}
              >
                Add service
              </Button>
            )}
          </div>
        )
      })()}

      {tab === 'build' && phone && !openVisitId && (
        <div className="flex-1 bg-bg px-4 pb-[190px] pt-3">
          {/* Absent, not disabled, on a quote that is not a draft (walkthrough findings §4). */}
          {canEdit && <div className="mb-3 flex gap-2">
            {/*
              * A plain button rather than <Button>, because the search sheet
              * hangs off this element and needs the ref. Everything else about
              * it is the touch Button's shape (§3.6: 44–52px on touch).
              */}
            {/*
              * Board 17 cell 1: `+ Add visit` and `+ Package`, 44 high. Add
              * visit opens the five starts as a sheet — the search is one of
              * them (`From a service…`), so it no longer needs its own button.
              * Plain buttons rather than <Button>, because the sheets hang off
              * these elements and need the refs.
              */}
            <button
              ref={searchBox}
              type="button"
              disabled={!canEdit}
              onClick={() => { anchor.current = searchBox.current; setEditing({ kind: 'addVisit' }) }}
              className="flex h-11 flex-1 items-center justify-center gap-1.5 rounded-touch border border-line
                         bg-surface text-[14px] font-semibold text-text disabled:text-text-faint"
            >
              <IconPlus size={15} /> Add visit
            </button>
            <span ref={packageBox} className="flex flex-1">
              <button
                type="button"
                disabled={!canEdit}
                onClick={() => { anchor.current = packageBox.current; setEditing({ kind: 'addPackage' }) }}
                className="flex h-11 flex-1 items-center justify-center gap-1.5 rounded-touch border border-line
                           bg-surface text-[14px] font-semibold text-text disabled:text-text-faint"
              >
                <IconPlus size={15} /> Package
              </button>
            </span>
          </div>}

          {quote.visits.length === 0
            ? (
              <EmptyState
                title="Nothing on this quote yet"
                body="A visit is a trip to the property. Add one, then put the services that
                      happen on it inside."
                action={canEdit
                  ? {
                    label: 'Add visit',
                    onClick: () => void mutate('/visits', { method: 'POST', body: {} }),
                  }
                  : undefined}
              />
            )
            : (
              <div className="flex flex-col gap-3">
                {quote.visits.map((visit, at) => (
                  <Fragment key={visit.id}>
                    {/* cb-17 cell 1: `↳ 1 business day after Diagnose is done` between the cards. */}
                    {followsAbove(visit, quote.visits[at - 1])
                      && <PhoneVisitConnector phrase={visit.phrase.card} />}
                    <PhoneVisitCard
                      visit={visit}
                      targetMarginPct={quote.totals.targetMarginPct}
                      onOpen={() => {
                        setOpenService(visit.services[0]?.id ?? null)
                        /* One service: straight to its screen (qb-10 · 4). */
                        openPhoneScreen(visit.id, visit.services.length === 1 ? visit.services[0]!.id : null)
                      }}
                      onMenu={canEdit ? (menuAt) => setMenu({ ...menuAt, items: visitMenu(visit) }) : undefined}
                      marginOpen={editing?.kind === 'margin' && editing.visitId === visit.id}
                      onMargin={(chip) => openMargin(visit.id, chip)}
                      choices={choicesOf(visit.id)}
                      onChoices={openVisit(visit.id, 'choices')}
                    />
                  </Fragment>
                ))}
              </div>
            )}
        </div>
      )}

      {tab === 'build' && !phone && (
        <>
          <Toolbar
            phone={phone}
            canEdit={canEdit}
            searchRef={searchBox}
            onSearch={() => { anchor.current = searchBox.current; setEditing({ kind: 'add' }) }}
            packageRef={packageBox}
            addVisitRef={addVisitBox}
            onAddVisit={() => { anchor.current = addVisitBox.current; setEditing({ kind: 'addVisit' }) }}
            onAddPackage={() => {
              anchor.current = packageBox.current
              setEditing({ kind: 'addPackage' })
            }}
            onAddChoices={() => void mutate('/choose-groups', { method: 'POST', body: {} })}
            railOn={railOn}
            /* Below 1180 there is no room for the rail at all, so no switch for it. */
            onRail={narrow ? undefined : () => setRailOn((on) => !on)}
            allCollapsed={allCollapsed}
            onCollapseAll={collapseAll}
          />

          <div
            className={cn('flex-1 bg-bg',
              railHidden
                ? 'px-4 pb-24 pt-3.5 xl:px-6'
                : 'grid grid-cols-[minmax(0,1fr)_340px] gap-5 px-6 pb-5 pt-3.5')}
          >
            <div className="flex min-w-0 flex-col gap-3.5">
              {quote.visits.length === 0 && quote.chooseGroups.length === 0
                ? (
                  <EmptyState
                    title="Nothing on this quote yet"
                    body="A visit is a trip to the property. Add one, then put the services
                          that happen on it inside."
                    action={canEdit
                      ? {
                        label: 'Add visit',
                        onClick: () => void mutate('/visits', { method: 'POST', body: {} }),
                      }
                      : undefined}
                  />
                )
                : (
                  <>
                    {/*
                      * **The plans card** (§9.2, qb-06): at the top of the
                      * Build tab, above the visits — the question the
                      * customer is asked comes before the trips that answer
                      * it, and its Price, Cost and Margin per choice are the
                      * first thing an estimator checks on a ladder.
                      */}
                    {quote.chooseGroups.map((group) => (
                      <PlansGrid
                        key={group.id}
                        group={group}
                        collapsed={collapsed.has(group.id)}
                        onToggle={() => toggle(group.id)}
                        onRemoveGroup={() => void mutate(
                          `/choose-groups/${group.id}`, { method: 'DELETE' })}
                        /*
                         * **+ Add visits ▾** (step 22 item 2.4): a visit in no
                         * question, or a new one, joins the box **in no
                         * choice** — the estimator ticks where it belongs.
                         */
                        addable={quote.visits.filter((visit) => !visit.chooseGroupId).map((visit) => ({
                          id: visit.id, name: visit.name, sub: visit.phrase.card, total: visit.total, role: visit.role,
                        }))}
                        onAddVisit={(visitId, name) => void (visitId
                          ? mutate(`/visits/${visitId}`,
                            { method: 'PATCH', body: { chooseGroupId: group.id, optionIds: [] } })
                          : mutate('/visits', {
                            method: 'POST',
                            body: { chooseGroupId: group.id, optionIds: [], ...(name ? { name } : {}) },
                          }))}
                        onLeave={(visitId) => void mutate(`/visits/${visitId}`,
                          { method: 'PATCH', body: { chooseGroupId: null, optionIds: [] } })}
                        onGoTo={(visitId) => document.getElementById(`visit-${visitId}`)
                          ?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
                        options={quote.chooseOptions}
                        choice={quote.totals.margin.byChoice.find((one) => one.groupId === group.id)}
                        targetPct={quote.totals.targetMarginPct}
                        onReword={(name) => void mutate(
                          `/choose-groups/${group.id}`, { method: 'PATCH', body: { name } })}
                        onReorder={(visitIds) => void mutate(
                          '/reorder', { method: 'POST', body: { visits: visitIds } })}
                        visits={quote.visits}
                        canEdit={canEdit}
                        /*
                         * The box is named with the ticks (step 22 item 2.2):
                         * unticking a visit's last choice leaves it in the box,
                         * in no choice, rather than taking it out — the row's
                         * ⋮ is the way out.
                         */
                        onMembership={(visitId, optionIds) => void mutate(
                          `/visits/${visitId}`,
                          { method: 'PATCH', body: { chooseGroupId: group.id, optionIds } })}
                        onAddPlan={(name, recommended) => void mutate(
                          '/options',
                          { method: 'POST', body: { groupId: group.id, name, ...(recommended ? { recommended } : {}) } })}
                        onRename={(optionId, name) => void mutate(
                          `/options/${optionId}`, { method: 'PATCH', body: { name } })}
                        onRecommend={(optionId, recommended) => void mutate(
                          `/options/${optionId}`,
                          { method: 'PATCH', body: { recommended } })}
                        onRemovePlan={(optionId, keepVisits) => void mutate(
                          `/options/${optionId}?keepVisits=${keepVisits}`, { method: 'DELETE' })}
                        onAllowNone={(allowNone) => void mutate(
                          `/choose-groups/${group.id}`,
                          { method: 'PATCH', body: { allowNone } })}
                      />
                    ))}
                    {/*
                      * Packages first, each wrapping the visits it produced,
                      * then everything somebody built by hand. A package's
                      * visits are not loose rows: the quantity and the start
                      * belong to the group, and drawing them apart from it
                      * would leave three cards to keep in step by hand.
                      */}
                    {quote.packages.map((instance) => (
                      <PackageGroup
                        key={instance.id}
                        instance={instance}
                        collapsed={collapsed.has(instance.id)}
                        onToggle={() => toggle(instance.id)}
                        onQty={(qty: number) => void mutate(
                          `/packages/${instance.id}`, { method: 'PATCH', body: { qty } })}
                        onEditMeasure={qb.openPackageMeasure(instance.id)}
                        measureOpen={editing?.kind === 'packageMeasure'
                          && editing.instanceId === instance.id}
                        onEdit={(at: HTMLElement) => {
                          anchor.current = at
                          setEditing({ kind: 'package', instanceId: instance.id })
                        }}
                        readOnly={!canEdit}
                        onDetach={() => void mutate(
                          `/packages/${instance.id}/detach`, { method: 'POST' })}
                        onMenu={(at: { x: number; y: number }) => setMenu({
                          ...at,
                          items: [{
                            key: 'remove',
                            label: 'Remove package',
                            tone: 'danger',
                            onSelect: () => void mutate(
                              `/packages/${instance.id}`, { method: 'DELETE' }),
                          }],
                        })}
                      >
                        {byInstance.get(instance.id)?.map((visit, at, list) => (
                          <Fragment key={visit.id}>
                            {followsAbove(visit, list[at - 1])
                              && <VisitConnector phrase={visit.phrase.card} />}
                            {renderVisit(visit, <PackageChip />)}
                          </Fragment>
                        ))}
                      </PackageGroup>
                    ))}

                    {loose.map((visit, at) => (
                      <Fragment key={visit.id}>
                        {followsAbove(visit, loose[at - 1])
                          && <VisitConnector phrase={visit.phrase.card} />}
                        {renderVisit(visit)}
                      </Fragment>
                    ))}

                  </>
                )}
            </div>

            {!railHidden && (
              /*
               * The rail scrolls with the page (step 21 item 6): it used to be
               * sticky with a scroll of its own, which was the other half of
               * the builder behaving like no other screen.
               */
              <aside className="flex w-[340px] shrink-0 flex-col gap-3.5 self-start pb-1">
                <QuoteTotalCard
                  groupsWithChoices={choiceGroups}
                  totals={quote.totals}
                  visits={quote.visits}
                  onOpenVisit={(visitId) => {
                    setSelected(visitId)
                    document.getElementById(`visit-${visitId}`)
                      ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
                  }}
                />
                <MarginCard
                  id="margin-card"
                  margin={quote.totals.margin}
                  scope={marginScope}
                  onScope={setMarginScope}
                  onFixCosts={fixCosts}
                  hasSubs={hasSubs}
                />
                <BeforeYouSendCard
                  checks={buildChecks}
                  onGo={goToCheck}
                  onReview={(visitId, at) => openMargin(visitId, at)}
                />
                <TermsCard
                  termsId={quote.termsId}
                  termsName={quote.termsName}
                  choices={quote.termsChoices}
                  canEdit={canEdit}
                  onChange={(termsId) => void mutate(
                    '/terms', { method: 'PATCH', body: { termsId } })}
                />
              </aside>
            )}
          </div>

          {/*
            * The iPad and the phone lose the rail, so the numbers it held move
            * to a bar that is always on screen. §7: a builder where the total
            * is three scrolls away is a builder people check by scrolling.
            */}
          {railHidden && !phone && (
            <StickyTotals
              quote={quote}
              phone={false}
              primary={stagePrimary(stage, blocking.length, busy)}
              onPreview={() => openTab('proposal')}
              open={summaryOpen}
              onOpen={setSummaryOpen}
              summary={summaryCards}
              billing={billing}
            />
          )}
        </>
      )}

      {/*
        * §7's sticky bar and tab bar, on every phone screen rather than only
        * the desktop Build tab. A builder where the total is three scrolls
        * away is a builder people check by scrolling.
        */}
      {phone && (
        <StickyTotals
          quote={quote}
          phone
          primary={stagePrimary(stage, blocking.length, busy)}
          onPreview={() => openTab('proposal')}
          open={summaryOpen}
          onOpen={setSummaryOpen}
          summary={summaryCards}
          billing={billing}
          /* qb-10 · 4: on a service's screen the bar is that visit's. */
          visit={tab === 'build' && phoneVisit && phoneService
            ? { total: phoneVisit.total, marginPct: phoneVisit.marginPct }
            : undefined}
        />
      )}
      {/*
        * One write for the three keys: two writes in a row each built from
        * the same render's params, so the second put back the `visit` the
        * first had taken out.
        */}
      {phone && <PhoneTabs tab={tab} onTab={(next) => qb.setParams((was) => {
        const copy = new URLSearchParams(was)
        copy.delete('visit')
        copy.delete('service')
        copy.set('tab', next as Tab)
        return copy
      }, { replace: true })} />}

      {tab === 'billing' && !phone && (() => {
        /* Every plan folded is the pill on; pressing it again opens them all. */
        const planIds = quote.plans.map((plan) => plan.id)
        const allPlansFolded = planIds.length > 0 && planIds.every((planId) => collapsed.has(planId))
        return (
          <BillingToolbar
            canEdit={canEdit}
            search={planSearch}
            onSearch={setPlanSearch}
            onNewPlan={(kind) => void mutate('/plans', { method: 'POST', body: { kind } })}
            railOn={railOn}
            onRail={narrow ? undefined : () => setRailOn((on) => !on)}
            allCollapsed={allPlansFolded}
            onCollapseAll={() => setCollapsed((was) => {
              const next = new Set(was)
              for (const planId of planIds) {
                if (allPlansFolded) next.delete(planId)
                else next.add(planId)
              }
              return next
            })}
          />
        )
      })()}

      {tab === 'billing' && (
        <div
          className={cn('flex-1 bg-bg',
            railHidden
              ? cn('px-4 pt-3.5 xl:px-6', phone ? 'pb-[190px]' : 'pb-24')
              : 'grid grid-cols-[minmax(0,1fr)_340px] items-start gap-5 px-6 pb-5 pt-3.5')}
        >
          <BillingTab
            quote={quote}
            canEdit={canEdit}
            busy={busy}
            onMutate={mutate}
            search={phone ? '' : planSearch}
            collapsed={collapsed}
            onToggle={toggle}
            onOpenVisit={(visitId) => {
              /*
               * Back to Build with that card selected. The Billing tab is
               * about when the money lands; the moment somebody clicks a
               * visit they have stopped asking that and started asking what
               * is on it.
               */
              setSelected(visitId)
              openTab('build')
              window.setTimeout(() => {
                document.getElementById(`visit-${visitId}`)
                  ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
              }, 0)
            }}
          />

          {!railHidden && (
            <aside className="flex w-[340px] shrink-0 flex-col gap-3.5">
              {billingRail.cash}
              {billingRail.checks}
              {billingRail.defaults}
            </aside>
          )}
        </div>
      )}

      {/*
        * The iPad's bar on the Billing tab too (§8.4, Board 15): what the plans
        * bill, and the rail's three cards behind its Summary.
        */}
      {tab === 'billing' && railHidden && !phone && (
        <StickyTotals
          quote={quote}
          phone={false}
          primary={stagePrimary(stage, blocking.length, busy)}
          onPreview={() => openTab('proposal')}
          open={summaryOpen}
          onOpen={setSummaryOpen}
          summary={summaryCards}
          billing={billing}
        />
      )}

      {tab === 'proposal' && (
        <div className={cn('flex-1 bg-bg py-5',
          phone && 'pb-[190px]')}>
          {proposalError !== null
            ? (
              <p className="m-0 px-4 text-[13.5px] text-danger">{proposalError}</p>
            )
            : proposal === null
              ? (
                <p className="m-0 px-4 text-[13.5px] text-text-muted">
                  Loading the proposal…
                </p>
              )
              : (
                <ProposalTab
                  quoteId={id}
                  view={proposal}
                  selection={selection ?? proposal.initial}
                  onSelection={setSelection}
                  canEdit={canEdit}
                  canHandle={canHandle}
                  busy={busy || refused.busy}
                  error={error}
                  phone={phone}
                  onChanged={setProposal}
                  /*
                   * Today's office approve (the typed name) is gone: the
                   * bar's Approve records a yes (folder 52 §5, Board 3 A).
                   * On a quote that is with the customer the sign area says
                   * so (§8 A, §14).
                   */
                  signNote={quote.status === 'sent' || quote.status === 'viewed'
                    ? PROPOSAL_TAB.signsOnLink(stage.actions?.firstName)
                    : null}
                />
              )}
        </div>
      )}

      {/* The shared activity component, as the job and the contact have it (findings §4). */}
      {tab === 'activity' && <QuoteActivity quoteId={quote.id} canEdit={canHandle} phone={phone} />}

      <QuoteEditorLayer qb={qb} me={me} />
      {/* Board 3's popups, opened from the bar, ⋯ and the sticky bar (§5). */}
      <QuoteStagePopups qb={qb} stage={stage} />
    </div>
  )

}

